import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import styled, { createGlobalStyle } from 'styled-components'
import { isEmpty, omit, path } from 'ramda'
import { gql, useLazyQuery, useQuery } from '@apollo/client'
import {
  Box,
  Button,
  Checkbox,
  IconBell,
  ResponsiveRender,
  useDialogControllers
} from '@machobear/design-system'

import structure from '@/structure'
import { breakpoints, colors } from '@/styles'
import { useNeighbourhoodData } from '@/lib/locations'
import { getUrlSlugs } from '@/utils/location'
import useDebounce from '@/hooks/use-debounce'
import { useFilters } from '@/hooks/use-filters'
import { useHeaderContext } from '@/hooks/use-header-context'
import useLocalStorage from '@/hooks/use-localstorage'
import useMediaQuery from '@/hooks/use-media-query'
import useSessionComplete from '@/hooks/use-session-complete'
import ListingFilter from '@/features/Listings/listing-filter'
import {
  BUILDING_DATA_QUERY,
  BuildingListModal
} from '@/features/Listings/map/building-list-modal'
import { AlertConfirmationModal } from '@/components/alert'
import { useImpressionTracker } from '@/components/ImpressionProvider/ImpressionProvider'
import { useListImpressionTracking } from '@/components/ImpressionProvider/useListImpressionTracking'
import DefaultMarker from '@/components/maplibre-map/default-marker'
import Map from '@/components/maplibre-map/map'
import MapStyler from '@/components/maplibre-map/map/map-styler'
import { mapContainerForListing } from '@/components/maplibre-map/map/styles.css'
import { hasBoundingBoxChanged } from '@/components/maplibre-map/utils'
import { useTermsDialogContext } from '@/components/terms-dialog/context'

import { CityListener } from './city-listener'
import useSyncMapBoundsToURL from './hooks/use-sync-map-bounds-to-url'
import { defaultCoords, mapZIndex, markerCounts, zoomLevels } from './lib'
import MapList from './map-list'
import { Actions } from './map-list/actions'
import { MessageModalProvider } from './map-list/context'
import MapListingCarousel from './map-listing-carousel'
import { useMapList } from './maplist-context'
import ListingMapPopup from './popup'

const CenterPointMarker = dynamic(() => import('./center-point-marker'), {
  ssr: false
})

const CitySelector = dynamic(() => import('@/features/CitySelector'))

const SearchWrapper = styled.div`
  background-color: ${colors.white};
  max-height: 500px;
  transition: all 0.15s ease-in-out;
  z-index: ${mapZIndex.filters + 1};

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    transform: ${props =>
      props.$isHeaderVisible ? 'translateY(0px)' : 'translateY(-72px)'};
    max-height: ${props => (props.$isHeaderVisible ? '500px' : '0')};
  }
`

const omittedFilters = omit([
  'page',
  'page_size',
  'featured',
  'sort',
  'cities',
  'availability_date',
  'custom_availability_date'
])

const MAP_SEARCH_QUERY = gql`
  query ($input: MapSearchInput!) {
    mapSearch(input: $input) {
      all(input: $input) {
        properties {
          count
          buildings
          cluster
          price
          is_group
          public_viewing
          sponsored
          show_label
        }
        geometry {
          coordinates
        }
      }
    }
  }
`

const FiltersWrapper = styled.div`
  position: sticky;
  top: ${structure.header.height}px;
  background-color: ${colors.white};
  z-index: ${mapZIndex.filters};
  padding: 12px 20px;
  border-top: 1px solid ${colors.grey88};
  border-bottom: 1px solid ${colors.grey88};

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    border-top: none;
    top: 0px;
    padding: 0;
  }
`

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  flex: 1 1 0%;
  position: relative;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
    right: 0px;
    height: ${props => {
      if (props.$isListVisible) {
        if (props.$isHeaderVisible) {
          return `calc(100dvh - ${structure.mobile.header}px - ${structure.mobile.search}px)`
        }
        return `100dvh`
      } else {
        return `calc(100dvh - ${structure.mobile.header}px - ${structure.mobile.search}px)`
      }
    }};
  }
`

const GlobalStyles = createGlobalStyle`
 .maplibregl-ctrl-bottom-right .maplibregl-ctrl {
    margin: 0 20px 20px 0;
  }

  .maplibregl-popup {
    z-index: ${mapZIndex.markerPopup};
  }

  .maplibregl-marker:hover {
    z-index: ${mapZIndex.hoveredMarker} !important;
  }

  .maplibregl-ctrl-group:not(:empty) {
    box-shadow: 0px 2px 1px 0px rgba(0, 0, 0, 0.05), 0px 0px 1px 0px rgba(0, 0, 0, 0.25);
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    body, html {
      overflow: hidden;
      position: fixed;
      width: 100%;
    }

    .maplibregl-ctrl-group {
      display: none;
    }
  }
`

const getDistanceBetween = (pointA, pointB) => {
  if (!pointB) return null
  const radlatA = (Math.PI * pointA[1]) / 180
  const radlatB = (Math.PI * pointB[1]) / 180
  const theta = pointA[0] - pointB[0]
  const radtheta = (Math.PI * theta) / 180
  let dist =
    Math.sin(radlatA) * Math.sin(radlatB) +
    Math.cos(radlatA) * Math.cos(radlatB) * Math.cos(radtheta)
  dist = Math.acos(dist)
  dist = (dist * 180) / Math.PI
  dist = dist * 60 * 1.1515
  return dist
}

// This helps prevent the overlapping of markers with equal/similar
// coordinates, however, it is not a perfect solution
const getSafeCoordinates = (current, next, index) => {
  const distanceBetween = getDistanceBetween(current, next)
  if (distanceBetween !== null && distanceBetween < 0.005) {
    const [lon, lat] = current
    const radius = 0.0002
    const newLon = lon + radius * Math.cos(Math.PI * index)
    const newLat = lat + radius * Math.sin(Math.PI * index)
    return [newLon, newLat]
  }
  return current
}

const DEFAULT_ZOOM = zoomLevels.default
const DEFAULT_ZOOM_HOOD = zoomLevels.defaultHood
const DEFAULT_COORDS = defaultCoords
const LOAD_DELAY = 1500

const ListingMap = () => {
  useListImpressionTracking({
    id: 'listings',
    minVisibleDuration: 0
  })
  const { t } = useTranslation()
  const mapRef = useRef(null)
  const mapLoadedRef = useRef(false)
  const justLoadedRef = useRef(false)
  const { addImpression } = useImpressionTracker()
  const router = useRouter()
  const locale = router?.locale
  const hasSession = useSessionComplete()
  const [markersLoaded, setMarkersLoaded] = useState(false)
  const [cityChanged, setCityChanged] = useState(false)
  const [zoom, setZoom] = useState(DEFAULT_ZOOM)
  const [bounds, setBounds] = useState({})
  const [lastSearchedBounds, setLastSearchedBounds] = useState({})
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false)
  const [showRedoSearch, setShowRedoSearch] = useState(false)
  const [isRedoSearchLoading, setIsRedoSearchLoading] = useState(false)
  const [filters, , , isFilterSet] = useFilters()
  const [highlightedBuilding, setHighlightedBuilding] = useState()
  const [visited, setVisited] = useLocalStorage('visited', [])
  const [buildingId, setBuildingId] = useState(null)
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const { isListVisible } = useMapList()
  const { isHeaderVisible } = useHeaderContext()
  const showLabelsOnAllMarkers = zoom >= zoomLevels.labelsOnAllMarkers

  const { citySlug, neighbourhoodSlug } = getUrlSlugs(router)
  const neighborhood = useNeighbourhoodData(neighbourhoodSlug, citySlug)

  useSyncMapBoundsToURL({
    bounds,
    justLoadedRef,
    setCityChanged,
    cityChanged,
    citySlug
  })

  const centerPointMarker = useMemo(
    () => (
      <>
        {neighborhood?.data?.location?.lat &&
        neighborhood?.data?.location?.lon ? (
          <CenterPointMarker
            latitude={Number(neighborhood.data.location.lat)}
            longitude={Number(neighborhood.data.location.lon)}
            type={neighborhood.data.type}
            locationInfo={neighborhood.data.location_info}
          />
        ) : null}
      </>
    ),
    [neighborhood.data]
  )

  const { visible, showModal, closeModal } = useDialogControllers(
    {
      buildingModal: false,
      alertModal: false
    },
    { stopPropagation: true }
  )

  const { isLoading, checkAgreementMismatch } = useTermsDialogContext()
  const isAgreementMismatched = checkAgreementMismatch('trreb_agreements')
  const [popupInfo, setPopupInfo] = useState(null)

  const [getMarkerData, { data: { buildingDetail = [] } = {} }] = useLazyQuery(
    BUILDING_DATA_QUERY,
    {
      fetchPolicy: 'network-only'
    }
  )

  const debouncedZoom = useDebounce(zoom, 300)
  const debouncedBounds = useDebounce(bounds, 300)

  const { previousData = {}, data: mapData = previousData } = useQuery(
    MAP_SEARCH_QUERY,
    {
      variables: {
        input: {
          zoom: Math.round(debouncedZoom) + 1,
          max_markers: isMobile
            ? markerCounts.max_markers_mobile
            : markerCounts.max_markers,
          max_sponsored_labels: isMobile
            ? markerCounts.max_sponsored_labels_mobile
            : markerCounts.max_sponsored_labels,
          max_total_labels: isMobile
            ? markerCounts.max_total_labels_mobile
            : markerCounts.max_total_labels,
          boundaries: {
            top_left: debouncedBounds.topLeft,
            bottom_right: debouncedBounds.bottomRight
          },
          ...omit(['page', 'page_size', 'featured', 'sort', 'cities'], filters)
        }
      },
      fetchPolicy: 'cache-first',
      skip: isEmpty(debouncedBounds),
      onCompleted: () => {
        setMarkersLoaded(true)
        setIsRedoSearchLoading(false)
      },
      onError: () => {
        setIsRedoSearchLoading(false)
      }
    }
  )

  const { mapSearch: { all = [] } = {} } = mapData

  const resetMapListPosition = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [])

  const onChange = useCallback(
    ({ type, target: map }) => {
      const mapBounds = map.getBounds()
      const formattedBounds = {
        topLeft: {
          lat: mapBounds.getNorthWest().lat,
          lon: mapBounds.getNorthWest().lng
        },
        bottomRight: {
          lat: mapBounds.getSouthEast().lat,
          lon: mapBounds.getSouthEast().lng
        }
      }
      const hasBoxChanged = hasBoundingBoxChanged(bounds, formattedBounds)

      // Update zoom immediately, as it might affect UI or clustering logic directly
      const zoomLevels = map.getZoom()
      setZoom(zoomLevels)

      // Handle initial load
      if (type === 'load') {
        if (isMobile) {
          map.resize()
        }
        setBounds(formattedBounds)
        setShowRedoSearch(false)
        setCityChanged(false)
        resetMapListPosition()
        mapLoadedRef.current = true
        justLoadedRef.current = true
        setTimeout(() => {
          justLoadedRef.current = false
        }, LOAD_DELAY)
        return
      }

      // Prevent updates during the brief period after initial load
      if (justLoadedRef.current) {
        return
      }

      // Handle user updating location via search bar (city changed)
      if (mapLoadedRef.current && cityChanged) {
        setBounds(formattedBounds)
        setShowRedoSearch(false)
        setTimeout(() => {
          setCityChanged(false)
        }, LOAD_DELAY)
        resetMapListPosition()
        return
      }

      // Handle map interaction (pan/zoom end) after load and not city change
      if (mapLoadedRef.current && (type === 'moveend' || type === 'zoomend')) {
        // Handle user pan/move map with auto refresh enabled or on mobile
        if (isMobile || autoRefreshEnabled) {
          setBounds(formattedBounds)
          resetMapListPosition()
          return
        }
        // Handle user pan/move map w auto refresh disabled
        // Don't call setBounds here. Store the potential new bounds
        // and show the redo search button. The actual fetch will only
        // happen via debouncedBounds when the user clicks "Redo Search"
        if (!hasBoxChanged) return
        // ^^ Prevent redo button from showing due to slight resize from filter changes
        setLastSearchedBounds(formattedBounds)
        setShowRedoSearch(true)
      }
    },
    [
      autoRefreshEnabled,
      resetMapListPosition,
      isMobile,
      cityChanged,
      setBounds,
      setZoom,
      setShowRedoSearch,
      setCityChanged,
      setLastSearchedBounds,
      bounds
    ]
  )

  // Effect to reset list position on mobile when the debounced bounds change
  useEffect(() => {
    // Only run on mobile and when debouncedBounds is populated (avoid initial empty state)
    if (isMobile && !isEmpty(debouncedBounds)) {
      // Check if map has loaded to avoid resetting before initial data fetch completes
      if (mapLoadedRef.current) {
        resetMapListPosition()
      }
    }
    // resetMapListPosition is stable due to useCallback
  }, [debouncedBounds, isMobile, resetMapListPosition])

  const handleRedoSearch = useCallback(() => {
    resetMapListPosition()
    setIsRedoSearchLoading(true)
    setBounds(lastSearchedBounds)
    setShowRedoSearch(false)
  }, [resetMapListPosition, setBounds, setShowRedoSearch, lastSearchedBounds])

  const handleAutoRefreshToggle = () => {
    window.dataLayer.push({
      event: `auto-refresh-map-toggle-${autoRefreshEnabled ? 'off' : 'on'}`
    })
    setAutoRefreshEnabled(!autoRefreshEnabled)
  }

  const setVisitedBuilding = useCallback(
    buildingId => {
      setBuildingId(buildingId)
      if (!visited.includes(buildingId)) {
        setVisited([...visited, buildingId])
      }
    },
    [setVisited, visited]
  )

  const filtered = omittedFilters(filters)

  const handleMarkerClick = useCallback(
    (position, { count, sponsored, building_id }) => {
      setPopupInfo(null)
      setVisitedBuilding(building_id)
      if (count > 1) {
        showModal('buildingModal')()
      } else {
        getMarkerData({
          variables: {
            input: {
              building_id,
              ...filtered
            }
          },
          onCompleted: data => {
            setPopupInfo({
              data: data.buildingDetail[0],
              latitude: position[0],
              longitude: position[1]
            })
            addImpression(data.buildingDetail[0].listing_id, {
              page: 'maps',
              section: 'map-popup',
              sponsored
            })
          }
        })
      }
    },
    [addImpression, filtered, getMarkerData, setVisitedBuilding, showModal]
  )

  const closeMobilePopup = () => {
    const filtered = omittedFilters(filters)
    getMarkerData({
      variables: {
        input: {
          building_id: null,
          ...filtered
        }
      }
    })
  }
  const selectedMobile = path(['0'], buildingDetail)
  const hasSelected = !!selectedMobile?.listing_id && isMobile

  const boundaries = bounds.topLeft
    ? {
        boundaries: {
          top_left: bounds.topLeft,
          bottom_right: bounds.bottomRight
        }
      }
    : {}

  const markers = useMemo(() => {
    if (isRedoSearchLoading) {
      return []
    }

    return all.map(
      ({ geometry: { coordinates }, properties }, i, buildings) => {
        const building_id = path(['buildings', 0], properties)
        const nextMarker = buildings[i + 1]

        const safeCoordinates = getSafeCoordinates(
          coordinates,
          nextMarker?.geometry?.coordinates,
          i
        )
        const [lon, lat] = safeCoordinates
        const position = [lat, lon]

        const isVisited = visited.includes(building_id)
        const isImported = properties.public_viewing === false
        const isLoadingAgreements = isLoading && hasSession

        const hidePrice =
          isImported &&
          (isAgreementMismatched || !hasSession || isLoadingAgreements)

        return (
          <DefaultMarker
            key={`${properties.buildings?.[0]}`}
            position={position}
            properties={properties}
            buildingId={building_id}
            showHoverEffect={properties.buildings?.includes(
              highlightedBuilding
            )}
            onClick={handleMarkerClick}
            hidePrice={hidePrice}
            visited={isVisited}
            locale={locale}
            showLabels={showLabelsOnAllMarkers}
          />
        )
      }
    )
  }, [
    all,
    handleMarkerClick,
    hasSession,
    highlightedBuilding,
    isAgreementMismatched,
    isLoading,
    visited,
    isRedoSearchLoading,
    locale,
    showLabelsOnAllMarkers
  ])

  return (
    <>
      {isMobile && (
        <>
          <SearchWrapper $isHeaderVisible={isHeaderVisible}>
            <Box style={{ padding: '4px 16px 12px' }}>
              <CitySelector instanceId="map-select-city" />
            </Box>
          </SearchWrapper>
        </>
      )}
      <GlobalStyles />
      {!isMobile && (
        <FiltersWrapper $isHeaderVisible={isHeaderVisible}>
          <ListingFilter
            isMapMode
            hideButtons={hasSelected}
            boundaries={boundaries}
          />
        </FiltersWrapper>
      )}
      <MapStyler hasHeader>
        <MessageModalProvider>
          <Wrapper
            $isHeaderVisible={isHeaderVisible}
            $isListVisible={isListVisible}
          >
            {isMobile && (
              <>
                <Actions boundaries={boundaries} filters={filters} />
                {isFilterSet && (
                  <FiltersWrapper $isHeaderVisible={isHeaderVisible}>
                    <ListingFilter
                      isMapMode
                      hideButtons={hasSelected}
                      boundaries={boundaries}
                    />
                  </FiltersWrapper>
                )}
                <Box
                  position="fixed"
                  bottom="0"
                  width="full"
                  background="white"
                  padding="xsmall"
                  borderTop="borderNeutralLight"
                  zIndex="sticky"
                >
                  <Button
                    icon={<IconBell />}
                    disabled={filters?.cities?.length < 1}
                    id={
                      isListVisible
                        ? 'city-page-create-alert-main-click'
                        : 'city-page-map-view-create-alert-main-click'
                    }
                    onClick={() => {
                      window.dataLayer.push({
                        event: 'create-alert-main-click'
                      })
                      showModal('alertModal')()
                    }}
                  >
                    {t('filters:b.listing.alert.create.button', 'Create Alert')}
                  </Button>
                </Box>
              </>
            )}
            <MapList
              boundingBox={bounds}
              boundaries={boundaries}
              setHighlightedBuilding={setHighlightedBuilding}
              setVisitedBuilding={setVisitedBuilding}
              markersLoaded={markersLoaded || isListVisible}
            />
            {!isListVisible && (
              <Box
                className={mapContainerForListing}
                style={{
                  height: `calc(100dvh - ${structure.header.height}px - ${
                    structure.header.filters
                  }px - ${isFilterSet ? structure.map.activeFilters : 0}px)`,
                  top: `calc(${structure.header.height}px + ${
                    structure.header.filters
                  }px + ${isFilterSet ? structure.map.activeFilters : 0}px)`
                }}
              >
                <Map
                  ref={mapRef}
                  center={DEFAULT_COORDS}
                  zoom={zoom}
                  onInit={onChange}
                  onChange={onChange}
                >
                  {markers}
                  {centerPointMarker}
                  <CityListener
                    zoomLevel={DEFAULT_ZOOM}
                    zoomLevelNeighborhood={DEFAULT_ZOOM_HOOD}
                    onLocationChange={() => setCityChanged(true)}
                  />
                  {!!popupInfo && !isMobile ? (
                    <ListingMapPopup
                      data={popupInfo.data}
                      latitude={popupInfo.latitude}
                      longitude={popupInfo.longitude}
                      onClose={() => setPopupInfo(null)}
                    />
                  ) : null}

                  <ResponsiveRender above="mobile">
                    <Box
                      position="absolute"
                      bottom="0"
                      left="0"
                      padding="medium"
                      style={{ zIndex: mapZIndex.updateControls }}
                    >
                      {showRedoSearch ? (
                        <Box borderRadius="small" boxShadow="small">
                          <Button
                            id="redo-search-button"
                            tone="brandAccent"
                            size="small"
                            onClick={handleRedoSearch}
                          >
                            {t('filters:Redo search in this area')}
                          </Button>
                        </Box>
                      ) : (
                        <Box
                          padding="xxsmall"
                          background="white"
                          borderRadius="small"
                          boxShadow="small"
                          zIndex="modal"
                        >
                          <Checkbox
                            id="auto-refresh-map-checkbox"
                            checked={autoRefreshEnabled}
                            size="small"
                            onChange={handleAutoRefreshToggle}
                            label={t('filters:Refresh as I move map')}
                          />
                        </Box>
                      )}
                    </Box>
                  </ResponsiveRender>
                </Map>
                {!!selectedMobile && isMobile && (
                  <MapListingCarousel
                    data={selectedMobile}
                    handleClose={closeMobilePopup}
                  />
                )}
              </Box>
            )}
            <BuildingListModal
              visible={visible.buildingModal}
              onClose={closeModal('buildingModal')}
              building_id={buildingId}
              building={null}
              filters={filtered}
              router={router}
              trackImpression={{
                page: 'maps',
                section: 'map-popup',
                sponsored: false
              }}
            />
            <AlertConfirmationModal
              isLoggedIn={hasSession}
              open={visible.alertModal}
              onClose={closeModal('alertModal')}
            />
          </Wrapper>
        </MessageModalProvider>
      </MapStyler>
    </>
  )
}

export default ListingMap
